import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://chaleur.online';
const DEFAULT_IMAGE = `${BASE_URL}/images/products/desert_sunset_flying.jpg`;
const BRAND = 'Chaleur';

// Static route → SEO data
// Every route MUST have a unique title (<60 chars) + description (<160 chars) so Google
// never sees two Chaleur URLs as duplicates.
const SEO_DATA = {
  '/': {
    title: 'Chaleur | Luxury Handcrafted Scarves, Wraps & Drapes from India',
    description: "Discover Chaleur's collection of luxury handwoven scarves, wraps and drapes. Each piece is made by India's finest artisans. Embrace giving, embody ownership.",
    keywords: 'luxury scarves India, handcrafted wraps, artisan textiles, silk scarves, cashmere wraps, ethical fashion',
  },
  '/shop': {
    title: 'Shop Luxury Scarves, Wraps & Drapes | Chaleur',
    description: 'Browse handwoven silk scarves, cashmere wraps and heritage drapes crafted by master artisans across India. Global shipping. Free above ₹5,000.',
    keywords: 'buy luxury scarves online, silk scarves India, cashmere wraps, designer scarves',
  },
  '/about': {
    title: 'Our Story — Where Warmth Meets Purpose | Chaleur',
    description: 'Chaleur exists to preserve India\'s heirloom weaving traditions while giving artisan families a sustainable, dignified livelihood.',
    keywords: 'about Chaleur, artisan story, sustainable luxury brand, Indian textiles',
  },
  '/the-craft': {
    title: 'The Craft — Ancient Weaving Techniques | Chaleur',
    description: 'From hand-spun pashmina to intricate zardozi, discover the 21 crafts behind every Chaleur piece — and the master weavers who preserve them.',
    keywords: 'handloom weaving India, pashmina, jamdani, zardozi, kantha embroidery, handwoven silk',
  },
  '/impact': {
    title: 'Our Impact — 200+ Artisan Families Empowered | Chaleur',
    description: 'Every Chaleur purchase supports 200+ artisan families and preserves endangered craft traditions. See our impact numbers and stories.',
    keywords: 'ethical fashion India, artisan livelihoods, fair trade textiles, sustainable luxury impact',
  },
  '/journal': {
    title: 'The Journal — Stories of Craft, Culture & Style | Chaleur',
    description: 'Read essays on Indian craftsmanship, styling guides, artisan profiles and our sustainability journey — updated weekly.',
    keywords: 'luxury fashion blog India, artisan stories, scarf styling tips, heritage crafts journal',
  },
  '/gifting': {
    title: 'Luxury Gifting — Corporate & Personal Scarves | Chaleur',
    description: 'Find the perfect luxury gift — personalised messages, signature packaging, and bespoke corporate gifting programmes.',
    keywords: 'luxury gifting India, corporate gifts scarves, wedding gifts, personalised gifting',
  },
  '/become-a-partner': {
    title: 'Chaleur Global Channel Partners — Local Hands. Global Stories.',
    description: 'A curated global platform for established craft entrepreneurs — bringing regional artistry to discerning markets while creating shared prosperity.',
    keywords: 'chaleur global partners, craft entrepreneur programme, channel partner India UAE UK, artisan collective wholesale',
  },
  '/partner-thank-you': {
    title: 'Thank You | Chaleur Partners',
    description: 'Your partner application has been received. Our partner desk will be in touch within three working days.',
    noindex: true,
  },
  '/corporate-enquiries': {
    title: 'Corporate & Bulk Gifting Enquiries | Chaleur',
    description: 'Bespoke corporate gifting from Chaleur — handcrafted scarves, wraps, art and gourmet pieces. Personalised at scale, delivered worldwide. MOQ 30 units.',
    keywords: 'corporate gifting India, bulk luxury gifts, client gifting scarves, employee gifting, corporate hampers, chaleur corporate orders',
  },
  '/chaleur-club': {
    title: 'Chaleur Club — Loyalty Rewards & Member Benefits | Chaleur',
    description: 'Join the Chaleur Club. Earn 1% back in points on every purchase, unlock priority access, exclusive previews and complimentary express shipping.',
    keywords: 'Chaleur Club loyalty, rewards programme India, luxury membership',
  },
  '/contact': {
    title: 'Contact Us — Customer Care & Concierge | Chaleur',
    description: 'Speak to our concierge — WhatsApp, email or call. Order help, styling advice, corporate enquiries and press.',
    keywords: 'Chaleur contact, customer care India, luxury concierge',
  },
  '/faq': {
    title: 'Frequently Asked Questions | Chaleur',
    description: 'Answers on shipping, returns, garment care, sizing, gifting and Chaleur Club — everything you need to know before you buy.',
    keywords: 'Chaleur FAQ, shipping questions, return policy, scarf care',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Chaleur',
    description: 'How Chaleur collects, stores and protects your personal information — plainly written, no legalese.',
    keywords: 'Chaleur privacy policy',
    noindex: false,
  },
  '/refund-policy': {
    title: 'Refund & Return Policy | Chaleur',
    description: '15-day easy returns on unused items in original condition. Read the full refund and exchange policy for domestic and international orders.',
    keywords: 'Chaleur refund policy, returns, exchange, 15-day return',
  },
  '/track-order': {
    title: 'Track Your Order | Chaleur',
    description: 'Track your Chaleur order in real time using your order number and email.',
    noindex: true,
  },
  '/artisans': {
    title: 'Meet the Artisans Behind Chaleur',
    description: 'Portraits and stories of the master weavers, embroiderers and dyers who craft every Chaleur piece by hand.',
    keywords: 'Chaleur artisans, master weavers India, handloom artisans',
  },
  // Private routes — noindex
  '/cart': { title: 'Shopping Cart | Chaleur', description: 'Review the pieces in your Chaleur cart.', noindex: true },
  '/checkout': { title: 'Checkout | Chaleur', description: 'Secure checkout for your Chaleur order.', noindex: true },
  '/account': { title: 'My Account | Chaleur', description: 'Your Chaleur account, orders and loyalty points.', noindex: true },
  '/login': { title: 'Sign In | Chaleur', description: 'Sign in to your Chaleur account.', noindex: true },
  '/register': { title: 'Create Account | Chaleur', description: 'Create your Chaleur account to earn loyalty points.', noindex: true },
  '/forgot-password': { title: 'Reset Password | Chaleur', description: 'Reset your Chaleur account password.', noindex: true },
  '/reset-password': { title: 'Reset Password | Chaleur', description: 'Set a new password for your Chaleur account.', noindex: true },
};

// Category-specific SEO for /shop/:category
const CATEGORY_SEO = {
  apparel: {
    title: 'Luxury Handwoven Apparel — Kaftans, Kurtas & More | Chaleur',
    description: 'Exquisite handwoven apparel — kaftans, kurtas and drapes made by master Indian artisans. Global shipping.',
  },
  accessories: {
    title: 'Handcrafted Scarves, Stoles & Wraps | Chaleur',
    description: 'Silk scarves, cashmere stoles and pashmina wraps — handwoven by heritage weavers. Free shipping over ₹5,000.',
  },
  'home-living': {
    title: 'Handwoven Home & Living Textiles | Chaleur',
    description: 'Cushion covers, throws and heirloom textiles for the home — hand-crafted with 100% natural fibres.',
  },
  'limited-editions': {
    title: 'Limited Edition Handwoven Pieces — Numbered & Signed | Chaleur',
    description: 'One-of-a-kind luxury textiles, numbered and signed by master weavers. Available while supplies last.',
  },
};

// -------- meta updaters --------

const setMeta = (name, content, isProperty = false) => {
  if (content === undefined || content === null) return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href, extraAttrs = {}) => {
  const selector = Object.entries(extraAttrs).reduce(
    (s, [k, v]) => `${s}[${k}="${v}"]`,
    `link[rel="${rel}"]`
  );
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeJsonLd = (attr) => {
  document.querySelectorAll(`script[type="application/ld+json"][${attr}]`).forEach((el) => el.remove());
};

const addJsonLd = (data, marker) => {
  removeJsonLd(marker);
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute(marker, 'true');
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
};

// -------- core updateSEO --------

export const updateSEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
}) => {
  document.title = title;
  setMeta('title', title);
  setMeta('description', description);
  if (keywords) setMeta('keywords', keywords);

  // Robots
  setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

  // Canonical
  setLink('canonical', url || BASE_URL);

  // Hreflang (India only per user preference)
  setLink('alternate', url || BASE_URL, { hreflang: 'en-in' });
  setLink('alternate', url || BASE_URL, { hreflang: 'x-default' });

  // Open Graph
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:image', image || DEFAULT_IMAGE, true);
  setMeta('og:url', url || BASE_URL, true);
  setMeta('og:type', type, true);
  setMeta('og:site_name', BRAND, true);

  // Twitter
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  setMeta('twitter:image', image || DEFAULT_IMAGE);
};

// -------- route hook (called once in Layout) --------

const cleanImageUrl = (img) => {
  if (!img) return DEFAULT_IMAGE;
  return img.startsWith('http') ? img : `${BASE_URL}${img}`;
};

const buildBreadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${BASE_URL}${it.path}`,
  })),
});

export const useSEO = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // Product / journal / artisan pages set their own SEO after fetch — skip here.
    if (path.startsWith('/product/') || path.startsWith('/journal/') || path.startsWith('/order-confirmation/')) {
      // still set canonical + noindex-off placeholder so we never inherit private-page noindex
      setMeta('robots', 'index, follow');
      setLink('canonical', `${BASE_URL}${path}`);
      return;
    }

    // Shop category
    if (path.startsWith('/shop/')) {
      const cat = path.split('/')[2];
      const catData = CATEGORY_SEO[cat];
      if (catData) {
        updateSEO({ ...catData, url: `${BASE_URL}${path}` });
        addJsonLd(
          buildBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Shop', path: '/shop' },
            { name: catData.title.split(' — ')[0] || cat, path },
          ]),
          'data-breadcrumb'
        );
        return;
      }
      // Unknown category slug → noindex so Google doesn't treat it as a duplicate homepage
      updateSEO({
        title: 'Shop | Chaleur',
        description: 'Browse our full collection of handwoven scarves, wraps and drapes.',
        url: `${BASE_URL}${path}`,
        noindex: true,
      });
      return;
    }

    // Admin routes — always noindex
    if (path.startsWith('/admin')) {
      updateSEO({
        title: 'Admin | Chaleur',
        description: 'Chaleur administration.',
        url: `${BASE_URL}${path}`,
        noindex: true,
      });
      return;
    }

    // Artisan detail
    if (path.startsWith('/artisans/')) {
      const slug = path.split('/')[2];
      updateSEO({
        title: `${slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} — Master Artisan | Chaleur`,
        description: `Meet the master artisan behind Chaleur's handwoven pieces — their story, craft and legacy.`,
        url: `${BASE_URL}${path}`,
      });
      return;
    }

    const seoData = SEO_DATA[path] || SEO_DATA['/'];
    updateSEO({ ...seoData, url: `${BASE_URL}${path}` });

    // Breadcrumb for known static pages
    if (path !== '/') {
      const label = seoData.title.split(' — ')[0].split(' | ')[0];
      addJsonLd(
        buildBreadcrumb([
          { name: 'Home', path: '/' },
          { name: label, path },
        ]),
        'data-breadcrumb'
      );
    } else {
      removeJsonLd('data-breadcrumb');
    }
  }, [location.pathname]);
};

// -------- Not-found SEO (kills Soft 404s) --------

/**
 * Called by page components when a dynamic resource (product/journal/etc)
 * cannot be loaded from the backend. Marks the page as noindex so Google
 * doesn't accumulate Soft 404s against these URLs.
 */
export const setNotFoundSEO = (label = 'Not Found') => {
  updateSEO({
    title: `${label} | Chaleur`,
    description: 'The page you are looking for could not be found.',
    url: `${BASE_URL}${typeof window !== 'undefined' ? window.location.pathname : '/'}`,
    noindex: true,
  });
  // Strip content-specific JSON-LD if any was already injected
  removeJsonLd('data-product-schema');
  removeJsonLd('data-article-schema');
  removeJsonLd('data-breadcrumb');
};

// -------- Product SEO --------

export const setProductSEO = (product) => {
  if (!product) return;

  const price = product.variants?.[0]?.price || 0;
  const image = cleanImageUrl(product.images?.[0]);
  const url = `${BASE_URL}/product/${product.slug}`;
  const description =
    product.meta_description ||
    product.short_description ||
    product.description?.slice(0, 155) ||
    `Shop ${product.name} — handwoven luxury from Chaleur`;

  updateSEO({
    title: product.meta_title || `${product.name} | Chaleur`,
    description,
    keywords: `${product.name}, ${product.craft_technique || 'handwoven'}, luxury scarf, Chaleur, ${(product.tags || []).join(', ')}`,
    image,
    url,
    type: 'product',
  });

  // Product JSON-LD
  addJsonLd(
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: (product.images || []).slice(0, 6).map(cleanImageUrl),
      description,
      sku: product.sku || product.id,
      brand: { '@type': 'Brand', name: BRAND },
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: 'INR',
        price,
        availability:
          (product.variants?.[0]?.inventory_quantity ?? 1) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
        seller: { '@type': 'Organization', name: BRAND },
      },
      ...(product.average_rating
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: product.average_rating,
              reviewCount: product.review_count || 1,
            },
          }
        : {}),
    },
    'data-product-schema'
  );

  // Breadcrumb JSON-LD
  addJsonLd(
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Shop', path: '/shop' },
      { name: product.name, path: `/product/${product.slug}` },
    ]),
    'data-breadcrumb'
  );
};

export const setJournalSEO = (post) => {
  if (!post) return;

  const image = cleanImageUrl(post.featured_image);
  const url = `${BASE_URL}/journal/${post.slug}`;
  const description = post.meta_description || post.excerpt || post.content?.slice(0, 155);

  updateSEO({
    title: post.meta_title || `${post.title} | Chaleur Journal`,
    description,
    keywords: (post.tags || []).join(', ') || 'fashion, craftsmanship, artisan',
    image,
    url,
    type: 'article',
  });

  addJsonLd(
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      image,
      author: {
        '@type': 'Person',
        name: post.author || 'Chaleur Editorial',
      },
      publisher: {
        '@type': 'Organization',
        name: BRAND,
        logo: {
          '@type': 'ImageObject',
          url: `${BASE_URL}/images/brand/chaleur-logo-clean.png`,
        },
      },
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      description,
      mainEntityOfPage: url,
    },
    'data-article-schema'
  );

  addJsonLd(
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Journal', path: '/journal' },
      { name: post.title, path: `/journal/${post.slug}` },
    ]),
    'data-breadcrumb'
  );
};

export default useSEO;
